import React, { FC } from 'react'
import { useQuery } from 'react-apollo'
import { makeStyles } from '@material-ui/core/styles'
import { PromptButton } from './dialog-parts'
import { useDialogStyles } from './styles'
import { SC_GET_WORKSPACES } from '../../../apollo/api/workspaces/queries'
import { setCurrentWorkspaceToLocalStorage } from '../../../helpers/local-storage'
import { DISPLAY_SUB_PATH } from '../../../constants/urlConstants'
import { logout } from '../../../helpers/authHelper'

interface Workspace {
  id: string
  name: string
}

const useExitStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    maxHeight: 176,
    overflowY: 'auto'
  }
})

// The lock covers the workspace switcher, and on a page with no workspace in the
// URL the CMS falls back to the stored one — the locked workspace. Storing the
// chosen workspace before a full page load (what the switcher itself does) is
// what gets a member out on every page.
export const goToWorkspace = (workspace: Workspace): void => {
  setCurrentWorkspaceToLocalStorage(workspace)
  window.location.href = `/workspace/${encodeURIComponent(workspace.id)}${DISPLAY_SUB_PATH}`
}

interface MemberLockExitsProps {
  // The locked workspace; unknown when the page has no workspace in its URL.
  lockedWorkspaceId?: string
}

// A member is not the one who owes: let them leave for their other workspaces.
export const MemberLockExits: FC<MemberLockExitsProps> = ({ lockedWorkspaceId }) => {
  const dialog = useDialogStyles()
  const classes = useExitStyles()
  const { data } = useQuery(SC_GET_WORKSPACES, { fetchPolicy: 'cache-first' })
  const others: Workspace[] = (data?.workspaces ?? []).filter(
    (workspace: Workspace) => workspace.id !== lockedWorkspaceId
  )

  if (!data) return null
  // The lock also covers the header's Logout: without this a member with nowhere
  // else to go could not even leave.
  if (!others.length) {
    return (
      <div>
        <p className={dialog.mutedCenter}>You have no other workspace to switch to.</p>
        <PromptButton
          onClick={() => logout()}
          variant='outline'
        >
          Sign out
        </PromptButton>
      </div>
    )
  }
  return (
    <div>
      <p className={dialog.mutedCenter}>You can keep working in another workspace:</p>
      <div
        aria-label='Your other workspaces'
        className={classes.list}
        role='group'
      >
        {others.map(workspace => (
          <PromptButton
            key={workspace.id}
            onClick={() => goToWorkspace(workspace)}
            variant='outline'
          >
            {`Go to ${workspace.name}`}
          </PromptButton>
        ))}
      </div>
    </div>
  )
}
